/**
 * Custom Sections Tab Component
 * Manages custom sections for reports (add, edit, delete, reorder, import)
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Badge, Modal } from '../UI';
import { useToastStore } from '../../stores/toast';
import {
  Plus,
  Edit,
  Trash2,
  GripVertical,
  Import,
  FileText,
  Brain,
  Type,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import {
  getReportSections,
  addCustomSection,
  updateCustomSection,
  deleteCustomSection,
  reorderSections,
  importSections,
  type ReportSection,
  type AddCustomSectionRequest,
  type UpdateCustomSectionRequest,
  type ImportSectionsRequest,
} from '../../api/reports';
import { SectionEditor } from './SectionEditor';
import { AddCustomSectionModal } from './AddCustomSectionModal';
import { SectionImportModal } from './SectionImportModal';
import { DraggableSectionList } from './DraggableSectionList';

interface CustomSectionsTabProps {
  reportId: string;
  report: any;
  onSectionUpdate?: () => void;
}

export function CustomSectionsTab({
  reportId,
  report,
  onSectionUpdate,
}: CustomSectionsTabProps) {
  const { addToast } = useToastStore();
  const queryClient = useQueryClient();
  const [editingSection, setEditingSection] = useState<ReportSection | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [draggedSection, setDraggedSection] = useState<ReportSection | null>(null);

  // Fetch sections
  const { data: sectionsData, isLoading, error, refetch } = useQuery({
    queryKey: ['report-sections', reportId],
    queryFn: () => getReportSections(reportId, { includeCustom: true, orderBy: 'display_order' }),
    enabled: !!reportId,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const sections = sectionsData?.sections || [];

  // Add section mutation
  const addMutation = useMutation({
    mutationFn: (request: AddCustomSectionRequest) => addCustomSection(reportId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-sections', reportId] });
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      setShowAddModal(false);
      addToast({ message: 'Custom section added successfully', type: 'success' });
      onSectionUpdate?.();
    },
    onError: (error: any) => {
      addToast({
        message: error.response?.data?.detail || 'Failed to add custom section',
        type: 'error',
      });
    },
  });

  // Update section mutation
  const updateMutation = useMutation({
    mutationFn: ({ sectionId, request }: { sectionId: string; request: UpdateCustomSectionRequest }) =>
      updateCustomSection(reportId, sectionId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-sections', reportId] });
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      setEditingSection(null);
      addToast({ message: 'Section updated successfully', type: 'success' });
      onSectionUpdate?.();
    },
    onError: (error: any) => {
      addToast({
        message: error.response?.data?.detail || 'Failed to update section',
        type: 'error',
      });
    },
  });

  // Delete section mutation
  const deleteMutation = useMutation({
    mutationFn: (sectionId: string) => deleteCustomSection(reportId, sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-sections', reportId] });
      queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      addToast({ message: 'Section deleted successfully', type: 'success' });
      onSectionUpdate?.();
    },
    onError: (error: any) => {
      addToast({
        message: error.response?.data?.detail || 'Failed to delete section',
        type: 'error',
      });
    },
  });

  // Reorder sections mutation
  const reorderMutation = useMutation({
    mutationFn: (sectionOrders: Array<{ section_id: string; display_order: number }>) =>
      reorderSections(reportId, { section_orders: sectionOrders }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-sections', reportId] });
      addToast({ message: 'Sections reordered successfully', type: 'success' });
      onSectionUpdate?.();
    },
    onError: (error: any) => {
      addToast({
        message: error.response?.data?.detail || 'Failed to reorder sections',
        type: 'error',
      });
    },
  });

  // Import sections mutation
  const importMutation = useMutation({
    mutationFn: (request: ImportSectionsRequest) => importSections(reportId, request),
    onSuccess: async () => {
      // Invalidate and refetch to ensure UI updates
      await queryClient.invalidateQueries({ queryKey: ['report-sections', reportId] });
      await queryClient.invalidateQueries({ queryKey: ['report', reportId] });
      // Explicitly refetch to ensure fresh data
      await refetch();
      setShowImportModal(false);
      addToast({ message: 'Sections imported successfully', type: 'success' });
      onSectionUpdate?.();
    },
    onError: (error: any) => {
      addToast({
        message: error.response?.data?.detail || 'Failed to import sections',
        type: 'error',
      });
    },
  });

  const handleEdit = (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (section) {
      setEditingSection(section);
    }
  };

  const handleDelete = (sectionId: string) => {
    if (confirm('Are you sure you want to delete this section?')) {
      deleteMutation.mutate(sectionId);
    }
  };

  const handleReorder = (newOrder: Array<{ sectionId: string; displayOrder: number }>) => {
    reorderMutation.mutate(
      newOrder.map((o) => ({ section_id: o.sectionId, display_order: o.displayOrder }))
    );
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <p className="text-gray-600">Failed to load sections</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Report Sections</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {sections.length} total sections ({sectionsData?.custom_count || 0} custom)
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowImportModal(true)}
            disabled={importMutation.isPending}
          >
            <Import className="h-4 w-4 mr-2" />
            Import from Previous
          </Button>
          <Button size="sm" onClick={() => setShowAddModal(true)} disabled={addMutation.isPending}>
            <Plus className="h-4 w-4 mr-2" />
            Add Custom Section
          </Button>
        </div>
      </div>

      {/* Sections List */}
      <DraggableSectionList
        sections={sections}
        reportId={reportId}
        onReorder={handleReorder}
        onEdit={handleEdit}
        onDelete={handleDelete}
        canEdit={true}
      />

      {/* Add Section Modal */}
      {showAddModal && (
        <AddCustomSectionModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          reportId={reportId}
          onSectionCreated={(section) => {
            addMutation.mutate({
              section_name: section.section_name,
              section_type: section.section_type || 'custom',
              content_mode: section.content_mode || 'ai',
              question: section.question,
              manual_content: section.answer,
            });
          }}
        />
      )}

      {/* Edit Section Modal */}
      {editingSection && (
        <SectionEditor
          section={editingSection}
          reportId={reportId}
          mode="edit"
          onSave={(updatedSection, regenerate = false) => {
            updateMutation.mutate({
              sectionId: updatedSection.id,
              request: {
                section_name: updatedSection.section_name,
                answer: updatedSection.answer,
                question: updatedSection.question,
                regenerate: regenerate, // Use explicit regenerate flag from SectionEditor
              },
            });
          }}
          onCancel={() => setEditingSection(null)}
        />
      )}

      {/* Import Sections Modal */}
      {showImportModal && (
        <SectionImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          targetReportId={reportId}
          projectId={report?.project_id}
          onImport={(request) => {
            importMutation.mutate(request);
          }}
        />
      )}
    </div>
  );
}
